import React, { useContext } from 'react';
import styled from '@emotion/styled';
import Pagination from '@atlaskit/pagination';
import Spinner from '@atlaskit/spinner';
import { ActionWrapper, ActionWrapperOuter, ActionWrapperInnerRight, ActionWrapperInnerLeft } from './ActionWrapper';
import { DebouncedTextfield } from './DebouncedTextfield';
import { GlobalContext } from './GlobalContext';
import { SearchBoxContainer, SearchBoxControlsContainer, SearchBoxFieldContainer } from '../stateless/layout/SearchBoxContainers';
import EmptyState from '@atlaskit/empty-state';

import { token } from '@atlaskit/tokens';

const colors = {
  tableTextGrey: token('color.background.accent.gray.subtle', '#5E6C84'),
  tableBorderGrey: token('color.text', '#DFE1E6'),
  tableTrHover: token('color.background.neutral.subtle.hovered', '#efefef'),
  tableSortActive: token('color.text.information'),
  tableSortInactive: token('color.background.accent.gray.subtle.pressed'),
  actionTd: token('color.text.subtlest', '#ccc'),
  loadingBg: token('elevation.shadow.overflow', '#fff'),
};

export const BetterTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin: 10px 0px;
  padding: 0px;
`;
export const BetterTableThead = styled.thead`
  border: 0px;
`;
export const BetterTableTbody = styled.tbody`
  border: 0px;
`;
export const BetterTableTr = styled.tr<{ doHover: boolean }>`
  margin-bottom: 5px;
  &:hover {
    background-color: ${(props) => (props.doHover ? colors.tableTrHover : 'transparent')};
  }
`;
export const BetterTableTd = styled.td`
  vertical-align: top;
`;
export const BetterTableTh = styled.th<{ widthPercent: string | undefined }>`
  vertical-align: top;
  text-align: left;
  width: ${(props) => (props.widthPercent ? props.widthPercent : 'inherit')};
`;

export const DynamicTableFix = styled.div`
  td {
    vertical-align: top !important;
  }
`;

export const BetterTableSort = styled.span<{ isSortable: boolean; alignRight: boolean | undefined }>`
  color: ${colors.tableTextGrey};
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'cursor')};
  user-select: none;
  display: flex;
  justify-content: ${(props) => (props.alignRight ? 'flex-end' : 'flex-start')};
  align-items: center;
  font-size: 12px;
  font-weight: 600;
`;
export const BetterTableSortText = styled.span`
  color: ${colors.tableTextGrey};
  margin-right: 3px;
`;

export const BetterTablePaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
`;
export const BetterTablePaginationWrapperLeft = styled.div``;
export const BetterTablePaginationWrapperCenter = styled.div``;
export const BetterTablePaginationWrapperRight = styled.div`
  color: ${colors.actionTd};
  font-size: 10px;
`;

export const BetterTableLoading = styled.div<{ isLoading: boolean }>`
  position: absolute;
  display: ${(props) => (props.isLoading ? 'block' : 'none')};
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  text-align: center;
  background: ${colors.loadingBg};
  z-index: 999999;
`;
export const BetterTableLoadingInner = styled.div`
  padding: 20px;
`;

export const BetterDynamicTable = ({
  head,
  rows,
  initialSort,
  onSortChanged,
  hasPages,
  initialPage,
  numPages,
  onPageChanged,
  processingTime,
  noResultsTitle,
  noResultsDescription,
  isLoading,
  hasFilter,
  initialFilter,
  onFilterChanged,
  actionCenter,
  actionLeft,
}: {
  head: BetterTableHead;
  rows: BetterTableRow[];
  initialSort: BetterTableSortItem;
  onSortChanged: (s: BetterTableSortItem) => void;
  hasPages: boolean;
  initialPage: number;
  numPages: number;
  onPageChanged: (newPage: number) => void;
  processingTime: number | null;
  noResultsTitle: string;
  noResultsDescription: string;
  isLoading: boolean;
  hasFilter: boolean;
  initialFilter?: string;
  onFilterChanged?: (f: string) => void;
  actionCenter?: React.ReactNode;
  actionLeft?: React.ReactNode;
}) => {
  const { t } = useContext(GlobalContext);
  const paginationPages = Array.from({ length: numPages }, (v, k) => k + 1);

  return (
    <>
      {hasFilter || actionCenter || actionLeft ? (
        <ActionWrapperOuter>
          <ActionWrapper>
            <ActionWrapperInnerLeft>{actionLeft ? actionLeft : null}</ActionWrapperInnerLeft>
            <ActionWrapperInnerRight>
              <div data-e2e-filter-searchfield="true" style={{ width: '100%' }}>
                {hasFilter && onFilterChanged ? (
                  <SearchBoxContainer>
                    <SearchBoxControlsContainer>
                      <div data-e2e-filter-searchfield-count="true" style={{ marginRight: '10px' }}>
                        &nbsp;
                      </div>
                    </SearchBoxControlsContainer>
                    <SearchBoxFieldContainer>
                      <DebouncedTextfield onFilterChanged={onFilterChanged} value={initialFilter} placeholder={t('global.searchFieldPlaceholder')} />
                    </SearchBoxFieldContainer>
                  </SearchBoxContainer>
                ) : null}
              </div>
            </ActionWrapperInnerRight>
          </ActionWrapper>
        </ActionWrapperOuter>
      ) : null}

      {/* NOTE: THE LOADING IS HOVERING OVER THE TABLE! */}
      <div style={{ position: 'relative', margin: '0px', padding: '0px' }}>
        <BetterTableLoading isLoading={isLoading}>
          <BetterTableLoadingInner>
            <Spinner size="medium" delay={0} />
          </BetterTableLoadingInner>
        </BetterTableLoading>
        <BetterTable>
          {rows.length !== 0 && (
            <BetterTableThead>
              <tr key={'head'}>
                {head.cells.map((cell) => (
                  <BetterTableTh widthPercent={cell.widthPercent} key={cell.key} style={{ whiteSpace: 'nowrap' }}>
                    <BetterTableSort
                      isSortable={cell.isSortable}
                      alignRight={cell.alignRight}
                      onClick={() => {
                        if (cell.isSortable && !isLoading) {
                          const s = {
                            key: cell.key,
                            sortOrder: initialSort.sortOrder === 'ASC' ? 'DESC' : 'ASC',
                          };
                          onSortChanged(s);
                        }
                      }}
                    >
                      <BetterTableSortText>{cell.content}</BetterTableSortText>
                      {cell.isSortable ? (
                        <svg viewBox="0 0 400 600" style={{ height: '10px' }}>
                          <polygon fill={initialSort.key === cell.key && initialSort.sortOrder === 'ASC' ? colors.tableSortActive : colors.tableSortInactive} points="200.5 18 379 262 22 262" />
                          <polygon
                            fill={initialSort.key === cell.key && initialSort.sortOrder === 'DESC' ? colors.tableSortActive : colors.tableSortInactive}
                            transform="translate(200.500000, 465.000000) scale(1, -1) translate(-200.500000, -465.000000) "
                            points="200.5 343 379 587 22 587"
                          />
                        </svg>
                      ) : (
                        ''
                      )}
                    </BetterTableSort>
                  </BetterTableTh>
                ))}
              </tr>
            </BetterTableThead>
          )}
          <BetterTableTbody>
            {rows.length === 0 && !isLoading ? (
              <BetterTableTr key={'noresults'} doHover={false}>
                <BetterTableTd colSpan={head.cells.length}>
                  <EmptyState header={noResultsTitle} description={noResultsDescription} />
                </BetterTableTd>
              </BetterTableTr>
            ) : null}
            {rows.map((row) => (
              <BetterTableTr key={row.key} doHover={row.doHoverTr}>
                {row.cells.map((cell) => (
                  <BetterTableTd key={cell.key} rowSpan={cell.rowSpan ? cell.rowSpan : 1}>
                    {cell.content}
                  </BetterTableTd>
                ))}
              </BetterTableTr>
            ))}
          </BetterTableTbody>
        </BetterTable>

        {rows.length !== 0 && !isLoading && (
          <BetterTablePaginationWrapper>
            <BetterTablePaginationWrapperLeft />
            <BetterTablePaginationWrapperCenter>
              {hasPages && rows.length !== 0 ? <Pagination pages={paginationPages} selectedIndex={initialPage - 1} onChange={(e: any, i: number) => onPageChanged(i)} /> : null}
            </BetterTablePaginationWrapperCenter>
            <BetterTablePaginationWrapperRight>{processingTime && <>{processingTime} ms</>}</BetterTablePaginationWrapperRight>
          </BetterTablePaginationWrapper>
        )}
      </div>
    </>
  );
};

export interface BetterTablePaginationState {
  currentPage: number; // = current page e.g. 1
  totalPages: number; // = num pages e.g. 3
  processingTimeInMs: number;
}
export const calculatePaginationState = (startAt: number, maxResults: number, totalResults: number, processingTimeInMs: number) => {
  const currentPage = Math.ceil(startAt / maxResults);
  let _currentPage: number;
  if (currentPage === 0) {
    _currentPage = 1;
  } else {
    _currentPage = currentPage + 1;
  }

  return {
    currentPage: _currentPage,
    totalPages: Math.ceil(totalResults / maxResults),
    processingTimeInMs: processingTimeInMs,
  } as BetterTablePaginationState;
};

export interface BetterTableHeadItem {
  key: string;
  content: React.ReactNode;
  isSortable: boolean;
  widthPercent: string;
  alignRight?: boolean;
}
export interface BetterTableHead {
  cells: BetterTableHeadItem[];
}
export interface BetterTableRowCell {
  key: string;
  content: React.ReactNode;
  rowSpan?: number;
}
export interface BetterTableRow {
  key: string;
  doHoverTr: boolean;
  cells: BetterTableRowCell[];
}
export class BetterTableSortItem {
  constructor(public key: string, public sortOrder: string) {}

  public static toUriPart(item?: BetterTableSortItem) {
    if (item) {
      let str = '';
      str = str + item.sortOrder === 'DESC' ? encodeURIComponent('-') : encodeURIComponent('+');
      str = str + item.key;
      return str;
    }
    return '';
  }
}
